import * as React from 'react'
import { FC } from 'react'
import { Dialog, DialogBody } from '@sales-center/components'

import { ReOpenPopupVariant } from './typings'
import { ReOpenAlerts } from './ReOpenAlerts'
import { DesktopDatePicker } from '@mui/x-date-pickers/DesktopDatePicker'
import Box from '@mui/material/Box'
import { useLocalizationDateFormat } from '@user/features/Localization/useLocalizationDateFormat'
import { Theme } from '@mui/material/styles'
import Button from '@mui/material/Button'
import Typography from '@mui/material/Typography'
import { useData } from '@user/components/Opportunities/ReOpenOppPopup/hooks/useData'
import { useActions } from '@user/components/Opportunities/ReOpenOppPopup/hooks/useActions'
import { OwnerSelect } from '@user/components/Leads/ReOpenDialog/OwnerSelect'
import { IReOpenFormModel } from '@user/components/Leads/ReOpenDialog/ReOpenLeadDialog'
import { Form, Formik } from 'formik'
import FormHelperText from '@mui/material/FormHelperText'

interface Props {
  onClose: () => void
  open: boolean
  opportunityId: number
  updateOppDetail?: boolean
  variant: ReOpenPopupVariant

  salesFunnelId: number
  salesTeamId: string
  assigneeId: string | null
}

export const ReOpenOppPopupNew: FC<Props> = (props) => {
  const {
    onClose,
    open,
    opportunityId,
    updateOppDetail,
    variant = 'basic',
  } = props

  const { getFormat } = useLocalizationDateFormat()

  const { owners, initialValues } = useData(props)

  const {
    handleReOpenPopupCancelClick,
    handleReOpenPopupOkButtonClick,
    validate,
    onSubmitForm,
  } = useActions({
    onClose,
    opportunityId,
    updateOppDetail,
  })

  return (
    <>
      <Dialog
        onClose={onClose}
        open={open}
        title={`Re-Open Opportunity`}
        width="xs"
        titleStyle={{
          padding: '24px',
          paddingBottom: 2,
        }}
        hideClose
      >
        <DialogBody>
          <Formik
            initialValues={initialValues}
            onSubmit={(values: IReOpenFormModel) => {
              handleReOpenPopupOkButtonClick(values)
            }}
            validate={validate}
          >
            {({
              values,
              setFieldValue,
              isSubmitting,
              submitForm,
              errors,
              submitCount,
              isValid,
              setFormikState,
            }) => (
              <Form style={{ padding: '0px 4px 4px' }}>
                {(variant === 'submitted' || variant === 'rejected') && (
                  <Typography variant="body2" mb={1}>
                    Re-opening this will remove your submission to the Closed
                    Won Queue.
                  </Typography>
                )}

                <Typography variant="body1" mb={1}>
                  Select a new Expected Closing Date
                </Typography>

                <DesktopDatePicker
                  data-test-id="expectedClosingDate"
                  disablePast={false}
                  format={getFormat()}
                  label="Expected Closing Date *"
                  onChange={(date) =>
                    setFieldValue('expectedClosingDate', date)
                  }
                  value={values.expectedClosingDate}
                  slotProps={{
                    textField: {
                      sx: {
                        width: '284px',
                      },
                      error: Boolean(
                        errors.expectedClosingDate && submitCount > 0,
                      ),
                    },
                    popper: {
                      placement: 'bottom-start',

                      modifiers: [
                        {
                          name: 'preventOverflow',
                          enabled: true,
                          options: {
                            altAxis: true,
                            tether: false,
                            rootBoundary: 'document',
                            padding: (theme: Theme) => theme.spacing(8),
                          },
                        },
                      ],
                    },
                  }}
                />

                {errors.expectedClosingDate && submitCount > 0 && (
                  <FormHelperText error>Required</FormHelperText>
                )}

                <Typography variant="body1" mb={1} mt={2}>
                  Select a new Owner
                </Typography>

                <Box sx={{ maxWidth: '284px' }}>
                  <OwnerSelect owners={owners} />
                </Box>

                <Box mt={1.2}>
                  <ReOpenAlerts {...props} />
                </Box>

                <Box mt={3} display="flex" justifyContent="flex-end">
                  <Button
                    sx={{ mr: 1 }}
                    onClick={handleReOpenPopupCancelClick}
                    variant="text"
                    data-test-id="opportunity-reopen-cancel-button"
                  >
                    Cancel
                  </Button>
                  <Button
                    color="primary"
                    data-test-id="opportunity-reopen-cancel-save-button"
                    disabled={isSubmitting}
                    variant="contained"
                    onClick={() =>
                      onSubmitForm(submitForm, isValid, setFormikState)
                    }
                  >
                    Save
                  </Button>
                </Box>
              </Form>
            )}
          </Formik>
        </DialogBody>
      </Dialog>
    </>
  )
}
